import dayjs from "dayjs";
import ExpressCheckinInfo, {IExpressCheckinInfoData} from "@/types/clients/esquire/data/general/contract-data/express-checkin-info";
import Guest, {IGuestData} from "@/types/clients/esquire/data/general/contract-data/guest";
import Unit, {IUnitData} from "@/types/clients/esquire/data/general/contract-data/unit";
import UnitType, {IUnitTypeData} from "@/types/clients/esquire/data/general/contract-data/unit-type";
import Option, {IOptionData} from "@/types/clients/esquire/data/general/contract-data/option";
import InvoiceLine, {IInvoiceLineData} from "@/types/clients/esquire/data/general/contract-data/invoice-line";
import Payment, {IPaymentData} from "@/types/clients/esquire/data/general/contract-data/payment";
import InvoiceLineMinified from "@/types/clients/esquire/data/general/contract-data/invoice-line-minified";
import _ from "lodash";
import FlatImage, {FlatImageData} from "@/types/clients/esquire/data/general/flat-image";
import DateService from "@/services/date-service";
import MoneyService from "@/services/money-service";
import { isCleaningSupplyOption } from "@/services/cleaning-supplies-service";

export interface IContractData {
  id: string;
  reservation_id: string;
  start_date: string;
  end_date: string;
  unit_id: string;
  unit_type_id: string;
  amount_of_guests: number;
  created_at: string;
  created_by: string;
  key_pickup_at?: string;
  key_pickup_by?: string;
  key_back_at?: string;
  key_back_by?: string;
  outgoing_state_at?: string;
  cancelled_at?: string;
  cancelled_because?: string;
  allow_without_payment: boolean;
  contractStatus?: string;

  express_checkin_info?: IExpressCheckinInfoData | null;
  guest: IGuestData;
  invoice_lines: IInvoiceLineData[];
  flat_image?: FlatImageData | null;
  options: IOptionData[];
  payments: IPaymentData[];
  unit?: IUnitData | null;
  unit_type: IUnitTypeData;


}


export default class Contract {
  public id: string;
  public reservationId: null|string;
  public startDate: string;
  public endDate: string;
  public unitId: string;
  public unitTypeId: string;
  public amountOfGuests: number;
  public createdAt: string;
  public createdBy: string;
  public keyPickupAt: null|string;
  public keyPickupBy: null|string;
  public keyBackAt: null|string;
  public keyBackBy: null|string;
  public outgoingStateAt: null|string;
  public cancelledAt: null|string;
  public cancelledBecause: null|string;
  public allowWithoutPayment: boolean;
  public contractStatus = "";

  public expressCheckinInfo: null|ExpressCheckinInfo;
  public guest: Guest;
  public unit: Unit|null;
  public unitType: UnitType;
  public options: Record<number, Option>;
  public payments: Payment[];
  public invoiceLines: InvoiceLine[];
  public flatImage: FlatImage|null;

  private readonly _accruedDebtToday: number;
  private readonly _accruedDebtBeforeCheckin: number;
  private readonly _accruedDebt: number;
  private readonly _paid: number;

  private readonly _nights: number;
  private readonly _optionNights: number;

  constructor(data: IContractData) {
    this.id = data.id;
    this.reservationId = data.reservation_id ?? null;
    this.startDate = data.start_date;
    this.endDate = data.end_date;
    this.unitId = data.unit_id;
    this.unitTypeId = data.unit_type_id;
    this.amountOfGuests = data.amount_of_guests;
    this.createdAt = data.created_at;
    this.createdBy = data.created_by;
    this.keyPickupAt = data.key_pickup_at ?? null;
    this.keyPickupBy = data.key_pickup_by ?? null;
    this.keyBackAt = data.key_back_at ?? null;
    this.keyBackBy = data.key_back_by ?? null;
    this.outgoingStateAt = data.outgoing_state_at ?? null;
    this.cancelledAt = data.cancelled_at ?? null;
    this.cancelledBecause = data.cancelled_because ?? null;
    this.allowWithoutPayment = data.allow_without_payment ?? false;

    this.expressCheckinInfo =  data.express_checkin_info ? new ExpressCheckinInfo(data.express_checkin_info) : null;
    this.guest = new Guest(data.guest);
    this.unit = data.unit ? new Unit(data.unit) : null;
    this.flatImage = data.flat_image ? new FlatImage(data.flat_image) : null;
    this.unitType = new UnitType(data.unit_type);

    this.options = _
      .chain(data.options)
      .map((data: IOptionData) => new Option(data))
      .keyBy((option: Option) => option.typeId)
      .value();

    this.payments = _.map(data.payments, (data: IPaymentData) => new Payment(data));

    this.invoiceLines = _.map(data.invoice_lines, (data: IInvoiceLineData) => new InvoiceLine(data));

    for (const invoiceLine of this.invoiceLines) {
      const optionTypeId = invoiceLine.optionTypeId;

      if (!optionTypeId) continue;

      const option = this.options[optionTypeId];

      if (!option) continue;

      option.isLocked = true;
      option.isSelected = true;
      option.isSelectedInitially = true;
    }

    this._accruedDebt = this.calculateAccruedDebt();
    this._accruedDebtToday = this.calculateAccruedDebtToday();
    this._accruedDebtBeforeCheckin = this.calculateAccruedDebtBeforeCheckin();
    this._paid = this.calculatePaid();

    this._nights = this.calculateNights();
    this._optionNights = this.calculateOptionNights();
  }


  toDTO(): IContractData {
    return {
      id: this.id,
      reservation_id: this.reservationId ?? "",
      start_date: this.startDate,
      end_date: this.endDate,
      unit_id: this.unitId,
      unit_type_id: this.unitTypeId,
      amount_of_guests: this.amountOfGuests,
      created_at: this.createdAt,
      created_by: this.createdBy,
      key_pickup_at: this.keyPickupAt ?? undefined,
      key_pickup_by: this.keyPickupBy ?? undefined,
      key_back_at: this.keyBackAt ?? undefined,
      key_back_by: this.keyBackBy ?? undefined,
      outgoing_state_at: this.outgoingStateAt ?? undefined,
      cancelled_at: this.cancelledAt ?? undefined,
      cancelled_because: this.cancelledBecause ?? undefined,
      allow_without_payment: this.allowWithoutPayment,

      express_checkin_info: this.expressCheckinInfo
        ? (this.expressCheckinInfo.toDTO?.() ?? this.expressCheckinInfo.toJson())
        : null,

      guest: this.guest.toDTO?.() ?? (this.guest.toJson() as IGuestData),

      invoice_lines: this.invoiceLines.map(il => il.toDTO?.() ?? (il.toDTO() as IInvoiceLineData)),
      flat_image: this.flatImage ? (this.flatImage.toDTO?.() ?? (this.flatImage.toDTO() as FlatImageData)) : null,

      options: Object.values(this.options).map(o => o.toDTO?.() ?? (o.toDTO() as IOptionData)),
      payments: this.payments.map(p => p.toDTO?.() ?? (p.toDTO() as IPaymentData)),

      unit: this.unit ? this.unit.toDTO?.() : null,
      unit_type: this.unitType.toDTO?.() ?? (this.unitType.toDTO() as IUnitTypeData),

      contractStatus: this.contractStatus || undefined
    };
  }

  get arrivesToday(): boolean {
    return DateService.toAppDate(this.startDate).isSame(DateService.today(), "day");
  }

  get departsToday(): boolean {
    return DateService.toAppDate(this.endDate).isSame(DateService.today(), "day");
  }

  get formattedArrivalDate(): string {
    return DateService.formatDate(this.startDate);
  }

  get formattedDepartureDate(): string {
    return DateService.formatDate(this.endDate);
  }

  get accruedDebt(): number {
    return this._accruedDebt;
  }
  get accruedDebtToday(): number {
    return this._accruedDebtToday;
  }
  get accruedDebtBeforeCheckin(): number {
    return this._accruedDebtBeforeCheckin;
  }

  get openBalance(): number {
    return MoneyService.round(this.accruedDebtToday - this.paid);
  }

  get nights(): number {
    return this._nights;
  }


  get beds(): number {
    if (!this.unit) return 0;
    return this.unit.singleBedCount + this.unit.doubleBedCount;
  }

  get optionNights(): number {
    return this._optionNights;
  }

  get paid(): number {
    return this._paid;
  }

  public getCombinedAddedAndRemovedOptions(): Option[] {
    const addedOptions = this.getAddedOptions().map(option => ({
      ...option,
      adjustedPrice: option.calculatePricePerMonth(this.nights, this.amountOfGuests)
    }));
    const removedOptions = this.getRemovedOptions().map(option => ({
      ...option,
      adjustedPrice: -option.calculatePricePerMonth(this.nights, this.amountOfGuests)
    }));

    return _.merge(addedOptions, removedOptions)
  }

  public calculateToPay(): number {
    return MoneyService.payable(this.accruedDebt - this.paid + this.getOptionPriceTotal());

  }

  public calculateToPayToday(): number {
    return MoneyService.payable(this.accruedDebtToday - this.paid + this.getOptionPricePerMonth());

  }

  public getAddedOptions(): Option[] {
    return _.filter(this.options, (o: Option) => !o.isSelectedInitially && o.isSelected);
  }

  private static readonly EARLY_CHECKIN_OPTION_TYPE_ID = 22;
  private static readonly LATE_CHECKOUT_OPTION_TYPE_IDS: number[] = [17, 18];

  private isEarlyCheckinOption(option: Option): boolean {
    return option.typeId === Contract.EARLY_CHECKIN_OPTION_TYPE_ID;
  }

  private isLateCheckoutOption(option: Option): boolean {
    return Contract.LATE_CHECKOUT_OPTION_TYPE_IDS.includes(option.typeId);
  }

  private isAvailableNonMandatoryOption(option: Option): boolean {
    const shouldHideEarlyCheckin = this.arrivesToday && this.isEarlyCheckinOption(option);
    const shouldHideLateCheckout = this.departsToday && this.isLateCheckoutOption(option);

    return !option.isMandatory()
      && !option.isBreakfast()
      && !isCleaningSupplyOption(option)
      && !shouldHideEarlyCheckin
      && !shouldHideLateCheckout;
  }

  public getNonMandatoryOptions(): Option[] {
    return _.filter(this.options, (option: Option) => this.isAvailableNonMandatoryOption(option));
  }

  public getBreakfastOption(): Option | null {
    return _.find(this.options, (option: Option) => {
      return option.typeId === 67 && !isCleaningSupplyOption(option);
    }) || null;
  }

  public getInvoiceLinesToPayBeforeCheckin(): InvoiceLine[] {
    return _.filter(this.invoiceLines, (i: InvoiceLine) => dayjs(this.startDate).isSame(i.payableAt) || dayjs(this.startDate).isAfter(i.payableAt) );
  }

  public getInvoiceLinesToPayToday(): InvoiceLine[] {
    return _.filter(this.invoiceLines, (i: InvoiceLine) => dayjs(i.payableAt).isBefore(dayjs()));
  }

  public getOptionPriceTotal(): number {
    const positivePrice = _.chain(this.getAddedOptions())
      .map((o: Option) => o.calculatePrice(this.optionNights, this.amountOfGuests))
      .sum()
      .value();

    const negativePrice = _.chain(this.getRemovedOptions())
      .map((o: Option) => o.calculatePrice(this.optionNights, this.amountOfGuests))
      .sum()
      .value();

    return positivePrice - negativePrice;
  }
  public getOptionPricePerMonth(): number {
    const positivePrice = _.chain(this.getAddedOptions())
      .map((o: Option) => o.calculatePricePerMonth(this.optionNights, this.amountOfGuests))
      .sum()
      .value();

    const negativePrice = _.chain(this.getRemovedOptions())
      .map((o: Option) => o.calculatePricePerMonth(this.optionNights, this.amountOfGuests))
      .sum()
      .value();

    return positivePrice - negativePrice;
  }
  public getOptionPricePerAmountOfNights(nights: number): number {
    const positivePrice = _.chain(this.getAddedOptions())
      .map((o: Option) => o.calculatePricePerMonth(nights, this.amountOfGuests))
      .sum()
      .value();

    const negativePrice = _.chain(this.getRemovedOptions())
      .map((o: Option) => o.calculatePricePerMonth(nights, this.amountOfGuests))
      .sum()
      .value();

    return positivePrice - negativePrice;
  }

  public getRemovedOptions(): Option[] {
    return _.filter(this.options, (o: Option) => o.isSelectedInitially && !o.isSelected);
  }

  private calculateAccruedDebt(): number {
    return _.chain(this.invoiceLines)
      .sumBy((i: InvoiceLine) => i.price)
      .value();
  }

  private calculateAccruedDebtToday(): number {
    const today = DateService.today();

    return _.chain(this.invoiceLines)
      .filter((i: InvoiceLine) => today.isSame(DateService.toAppDate(i.payableAt), "day") || today.isAfter(DateService.toAppDate(i.payableAt), "day"))
      .sumBy((i: InvoiceLine) => i.price)
      .value();
  }

  private calculateAccruedDebtBeforeCheckin(): number {
    const startDate = DateService.toAppDate(this.startDate);

    return _.chain(this.invoiceLines)
      .filter((i: InvoiceLine) => startDate.isSame(DateService.toAppDate(i.payableAt), "day") || startDate.isAfter(DateService.toAppDate(i.payableAt), "day"))
      .sumBy((i: InvoiceLine) => i.price)
      .value();
  }

  private calculateNights(): number {
    return DateService.diffDays(this.endDate, this.startDate);
  }

  private calculateOptionNights(): number {
    const startDate = DateService.toAppDate(this.startDate).startOf("day");
    const today = DateService.today();
    const optionStartDate = today.isAfter(startDate) ? today : startDate;

    return DateService.toAppDate(this.endDate).startOf("day").diff(optionStartDate, "days");
  }

  private calculatePaid(): number {
    return _.sumBy(this.payments, (p: Payment) => p.amount);
  }

  private synchronizeOptionPricesWithExistingInvoiceLines(): void {
    const invoiceLinesByOptionTypeId = _.chain(this.invoiceLines)
      .filter((il: InvoiceLine) => !!il.optionTypeId)
      .orderBy([(il: InvoiceLine) => il.startDate], ["asc"])
      .groupBy((il: InvoiceLine) => il.optionTypeId as number)
      .mapValues((ils: InvoiceLine[]) => _.map(ils, (il: InvoiceLine) => new InvoiceLineMinified(
        il.calculateDays(),
        il.price,
        il.guestCount
      )))
      .mapValues((ilrs: InvoiceLineMinified[]) => _.reduce(ilrs, (result: InvoiceLineMinified, ilr: InvoiceLineMinified) => {
        if (!result) return ilr;

        result.days += ilr.days;
        result.price += ilr.price;
        result.guests = Math.round((result.guests + ilr.guests) / 2);

        return result;
      }))
      .value();

    for (const typeId in this.options) {
      const invoiceLineMinified = invoiceLinesByOptionTypeId[typeId];

      if (!invoiceLineMinified) continue;

      const option = this.options[typeId];

      this.options[typeId].price = option.calculatePriceReversed(
        invoiceLineMinified.days, invoiceLineMinified.guests, invoiceLineMinified.price
      );

    }
  }
}
